import { useEffect, useState } from 'react'

import QuestionMark from '@/common/QuestionMark'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatAddress, formatBridgeAtomic } from '@/helpers'
import { cn } from '@/theme/utils'
import { NftMetadata } from '@/types'
import { UiSkeleton } from '@/ui/UiSkeleton'

import NftDefaultAvatar from '../NftDefaultAvatar'
import { BecomeValidatorDetailsStep } from './BecomeValidatorDetailsStep'
import { useBecomeValidatorShardsPayload } from './hooks/use-become-validator-shards-payload'

function SkeletonHeader() {
  return (
    <div className='flex items-center justify-between gap-4 text-xs'>
      <UiSkeleton className='h-4 w-28' />
      <UiSkeleton className='h-4 w-24' />
    </div>
  )
}

function NftSkeletonList({
  count,
  maxHClass,
}: {
  count: number
  maxHClass: string
}) {
  return (
    <div className={`max-h-[280px] overflow-auto pr-1 ${maxHClass}`}>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={`eligible-skel-${index}`}
            className='border-muted flex h-[52px] items-center gap-3 rounded-md border px-2 py-2'
          >
            <UiSkeleton className='h-8 w-8 rounded-md' />
            <div className='min-w-0 flex-1 space-y-1'>
              <UiSkeleton className='h-3 w-32' />
              <UiSkeleton className='h-3 w-24' />
            </div>
            <UiSkeleton className='h-4 w-4 rounded-sm' />
          </div>
        ))}
      </div>
    </div>
  )
}

function SkeletonFooter() {
  return (
    <div className='space-y-2 pt-1'>
      <UiSkeleton className='h-10 w-full rounded-md' />
      <UiSkeleton className='h-10 w-full rounded-md' />
      <UiSkeleton className='h-10 w-full rounded-md' />
    </div>
  )
}

type Step = 1 | 2

export function BecomeValidatorShards({
  selectedAddresses,
  onToggleAddress,
  onClose,
  onSuccess,
  renderTitle,
  renderFooter,
}: {
  selectedAddresses: string[]
  onToggleAddress: (addr: string) => void
  onClose: () => void
  onSuccess?: () => void
  renderTitle: (step: Step) => React.ReactNode
  renderFooter: (
    step: Step,
    opts: {
      canProceed: boolean
      isSubmitting: boolean
      onNext: () => void
      onBack?: () => void
    },
  ) => React.ReactNode
}) {
  const { address } = useKeplrWallet()
  const [step, setStep] = useState<Step>(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setStep(1)
    setIsSubmitting(false)
  }, [])

  useEffect(() => {
    if (step === 1) setIsSubmitting(false)
  }, [step])

  const {
    eligibleNfts,
    metadataMap,
    minimalDelegationAmount,
    selectedTotalAtomic,
    remainingAtomic,
    hasEnoughDelegation,
    remainingColorClass,
    isChecking,
    isCheckingValidators,
    isLoadingSelectedBalances,
  } = useBecomeValidatorShardsPayload(address, selectedAddresses)

  const canProceedToDetails =
    selectedAddresses.length > 0 &&
    hasEnoughDelegation &&
    !isChecking &&
    !isLoadingSelectedBalances

  return (
    <>
      {renderTitle(step)}

      <div className='text-foreground mt-4 flex flex-col gap-4 p-1 pr-4'>
        {step === 1 ? (
          <SelectShardsStep
            selectedAddresses={selectedAddresses}
            onToggleAddress={onToggleAddress}
            eligibleCount={eligibleNfts.length}
            isChecking={isChecking}
            isCheckingValidators={isCheckingValidators}
            eligibleNfts={eligibleNfts}
            metadataMap={metadataMap}
            minimalDelegationAmount={minimalDelegationAmount}
            selectedTotalAtomic={selectedTotalAtomic}
            remainingAtomic={remainingAtomic}
            hasEnoughDelegation={hasEnoughDelegation}
            remainingColorClass={remainingColorClass}
          />
        ) : (
          <BecomeValidatorDetailsStep
            selectedAddresses={selectedAddresses}
            onSuccess={onSuccess ?? onClose}
            onSubmittingChange={setIsSubmitting}
          />
        )}
      </div>

      {renderFooter(step, {
        canProceed: canProceedToDetails,
        isSubmitting,
        onNext: () => setStep(2),
        onBack: () => setStep(1),
      })}
    </>
  )
}

function SelectShardsStep({
  selectedAddresses,
  onToggleAddress,
  eligibleCount,
  isChecking,
  isCheckingValidators,
  eligibleNfts,
  metadataMap,
  minimalDelegationAmount,
  selectedTotalAtomic,
  remainingAtomic,
  hasEnoughDelegation,
  remainingColorClass,
}: {
  selectedAddresses: string[]
  onToggleAddress: (addr: string) => void
  eligibleCount: number
  isChecking: boolean
  isCheckingValidators: boolean
  eligibleNfts: { address: string; uri: string }[]
  metadataMap: Map<string, NftMetadata | undefined>
  minimalDelegationAmount: string
  selectedTotalAtomic: string
  remainingAtomic: string
  hasEnoughDelegation: boolean
  remainingColorClass: string
}) {
  return (
    <>
      {(() => {
        if (isChecking) {
          return (
            <div className='space-y-3'>
              <SkeletonHeader />
              <NftSkeletonList count={8} maxHClass='md:max-h-[220px]' />
              <SkeletonFooter />
            </div>
          )
        }

        if (eligibleNfts.length === 0 && isCheckingValidators) {
          return (
            <div className='space-y-3'>
              <SkeletonHeader />
              <NftSkeletonList count={10} maxHClass='md:max-h-[140px]' />
              <SkeletonFooter />
            </div>
          )
        }

        if (eligibleNfts.length === 0) {
          return (
            <div className='mt-8 mb-12 ml-4 flex flex-col items-center gap-5'>
              <div className='text-muted-foreground text-sm'>
                <div className='text-primary bg-primary-foreground aspect-square w-[100px] rounded-xl p-6'>
                  <QuestionMark />
                </div>
              </div>
              <h3>No eligible Shards found</h3>
            </div>
          )
        }

        return (
          <div className='space-y-3'>
            <div className='text-muted-foreground flex items-center justify-between gap-4 text-xs'>
              <span>Available: {eligibleCount}</span>
              <span>Selected: {selectedAddresses.length}</span>
            </div>

            <div className='max-h-[280px] overflow-auto pr-1 md:max-h-[220px]'>
              <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                {eligibleNfts.map(nft => {
                  const metadata = metadataMap.get(nft.uri)
                  const isSelected = selectedAddresses.includes(nft.address)

                  return (
                    <div
                      key={nft.address}
                      role='button'
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => onToggleAddress(nft.address)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onToggleAddress(nft.address)
                        }
                      }}
                      className={cn(
                        'text-foreground bg-primary/10 border-muted flex h-[52px] cursor-pointer items-center gap-3 rounded-md border px-2 py-2',
                        isSelected && 'bg-primary/20',
                      )}
                    >
                      {(() => {
                        const placeholderImageSrc = `${import.meta.env.BASE_URL}images/nft-preview.png`

                        if (!placeholderImageSrc) {
                          return (
                            <NftDefaultAvatar
                              className='h-8 w-8 rounded-md'
                              seed={nft.address}
                            />
                          )
                        }

                        return (
                          <img
                            alt={metadata?.name ?? 'NFT'}
                            src={placeholderImageSrc}
                            className='h-8 w-8 rounded-md object-cover'
                          />
                        )
                      })()}

                      <div className='min-w-0 flex-1'>
                        <div className='truncate text-sm font-medium'>
                          {metadata?.name || 'Unknown Shard'}
                        </div>
                        <div className='text-muted-foreground text-xs'>
                          {formatAddress(nft.address, 12, 10)}
                        </div>
                      </div>

                      <div
                        aria-hidden
                        className={cn(
                          'ml-2 grid h-4 w-4 shrink-0 place-items-center rounded-lg border bg-transparent',
                          isSelected
                            ? 'border-primary/70'
                            : 'border-primary/20',
                        )}
                      >
                        {isSelected && (
                          <span className='bg-primary/90 block h-2 w-2 rounded-sm' />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='space-y-2 pt-1'>
              <div className='space-y-2'>
                <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
                  <span className='text-muted-foreground text-xs'>
                    Required
                  </span>
                  <span className='font-semibold'>
                    {formatBridgeAtomic(minimalDelegationAmount)} BRIDGE
                  </span>
                </div>
                <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
                  <span className='text-muted-foreground text-xs'>
                    Selected
                  </span>
                  <span className='font-semibold'>
                    {formatBridgeAtomic(selectedTotalAtomic)} BRIDGE
                  </span>
                </div>
                <div className='bg-primary/10 flex items-center justify-between rounded-md px-3 py-2'>
                  <span className='text-muted-foreground text-xs'>
                    Remaining
                  </span>
                  <span className={cn('font-semibold', remainingColorClass)}>
                    {formatBridgeAtomic(remainingAtomic, 18)} BRIDGE
                  </span>
                </div>
              </div>

              {!hasEnoughDelegation && (
                <div className='text-muted-foreground text-xs'>
                  Select more Shards to reach the required amount.
                </div>
              )}

              {hasEnoughDelegation && (
                <div className='text-xs font-medium text-[#16D1D6]'>
                  You selected enough to become a validator
                </div>
              )}
            </div>
          </div>
        )
      })()}
    </>
  )
}
